import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { T, SERIF, STATUS, SERIES } from "../theme/tokens.js";
import { Panel, Empty, Label } from "./ui/index.jsx";
import { useRegisters } from "../lib/useRegister.js";
import { fmtMoney, fmtK, fmtDate, today } from "../lib/format.js";
import {
  vendorRollup, highRisks, isOpenRisk, budgetPosture, teamStats,
  upcomingWindow, invoiceSummary, isOverdue, forecastAtCompletion, programPosture,
} from "../lib/calc.js";

/* The executive Overview.
 *
 * Not the PM's cockpit, which lives on the Contracts workspace and lets a
 * person open every figure. This is the page a senior leader opens once a
 * month: who is on it, how much of them we have, where the money sits, is it
 * on track. It reads at a glance and it does not ask to be worked.
 *
 * Everything here is derived live from the same rows every other screen uses.
 * Nothing on this page is stored. A number that looks wrong is a row that is
 * wrong, and the place to fix it is the register, not here.
 */

/* ---- small building blocks, sized for a glance rather than a table ---- */

function Stat({ label, value, sub, color = T.navy, accent }) {
  return (
    <div style={{
      background: T.panel, border: `1px solid ${T.line}`,
      borderLeft: `4px solid ${accent || color}`, borderRadius: 6,
      padding: "16px 18px", minWidth: 0,
    }}>
      <Label color={T.navy}>{label}</Label>
      <div style={{
        fontFamily: SERIF, fontSize: 30, fontWeight: 700, lineHeight: 1.05,
        marginTop: 6, color, overflowWrap: "anywhere",
      }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.soft, marginTop: 4, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

/* A labelled share bar, the workhorse of a BI page: a person's allocation, a
   person's hours burned, a phase of budget. One meaning per colour. */
function Meter({ pct, color, track = T.chip, height = 10 }) {
  const w = Math.max(0, Math.min(100, pct ?? 0));
  return (
    <div style={{ background: track, borderRadius: 999, height, overflow: "hidden", width: "100%" }}>
      <div style={{ width: `${w}%`, height: "100%", background: color, borderRadius: 999 }} />
    </div>
  );
}

export default function ExecutiveOverview({ contract }) {
  const { data } = useRegisters(
    ["deliverables", "risks", "invoices", "expenses", "resources", "vendors", "milestones"],
    contract.id,
  );

  const model = useMemo(() => {
    if (!data) return null;
    const rollup = vendorRollup(data.deliverables);
    const budget = budgetPosture(contract, data.expenses, data.resources);
    const team = teamStats(data.resources);
    const high = highRisks(data.risks, contract);
    const openRisks = data.risks.filter(isOpenRisk);
    const invoices = invoiceSummary(data.invoices, {
      vendors: data.vendors, contractTerms: contract.payment_terms_days,
    });
    const overdueCDs = data.deliverables.filter(
      (d) => isOverdue(d) && d.is_contract_deliverable).length;
    const upcoming = upcomingWindow(
      { milestones: data.milestones, deliverables: data.deliverables }, 60);
    const forecast = forecastAtCompletion({
      ceiling: budget.ceiling, spent: budget.spent,
      percentComplete: rollup.total.percentComplete,
    });
    return {
      rollup, budget, team, high, openRisks, invoices, overdueCDs, upcoming, forecast,
      posture: programPosture({
        budgetLevel: budget.level, forecastLevel: forecast.level,
        overdueCDs, highRisks: high.length,
      }),
    };
  }, [data, contract]);

  if (!model) return <Empty text="Loading..." />;

  const { rollup, budget, team, high, openRisks, invoices, overdueCDs, upcoming, forecast, posture } = model;
  const t = rollup.total;

  /* Deliverable mix for the donut, in the fixed state order so the colours
     mean the same thing they mean everywhere. Overdue is pulled out of the
     other three rather than double counted. */
  const slices = [
    { name: "Complete", value: t.complete, fill: SERIES.complete },
    { name: "In progress", value: t.inProgress, fill: SERIES.inProgress },
    { name: "Not started", value: Math.max(0, t.notStarted - t.overdue), fill: SERIES.notStarted },
    { name: "Overdue", value: t.overdue, fill: SERIES.overdue },
  ].filter((s) => s.value > 0);

  const ref = today();
  const ceiling = budget.ceiling;
  const remaining = ceiling ? Math.max(0, ceiling - budget.spent) : 0;

  /* Program timeline, start to end, with today marked. Pure arithmetic on the
     two contract dates; no schedule engine needed for a leadership glance. */
  const timeline = (() => {
    if (!contract.start_date || !contract.end_date) return null;
    const a = new Date(contract.start_date).getTime();
    const b = new Date(contract.end_date).getTime();
    const n = new Date(ref).getTime();
    if (!(b > a)) return null;
    return { pct: Math.max(0, Math.min(100, Math.round(((n - a) / (b - a)) * 100))) };
  })();

  const topTeam = team.people.slice(0, 6);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Identity band, navy, the one place this page announces itself. */}
      <div style={{
        background: T.navy, color: "#fff", borderRadius: 8, padding: "20px 24px",
        display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "space-between", alignItems: "flex-start",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                        color: T.gold, fontWeight: 700 }}>
            {contract.client || "Program"}{contract.contract_number ? ` · ${contract.contract_number}` : ""}
          </div>
          <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 700, marginTop: 4 }}>
            {contract.name}
          </div>
          <div style={{ fontSize: 13, color: "#C9D2E0", marginTop: 6 }}>
            {contract.contract_type ? `${contract.contract_type} · ` : ""}
            {contract.start_date ? fmtDate(contract.start_date) : "start not set"}
            {" to "}
            {contract.end_date ? fmtDate(contract.end_date) : "end not set"}
          </div>
        </div>
        <div style={{
          background: posture.color, color: "#fff", borderRadius: 999,
          padding: "7px 16px", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
        }}>{posture.label}</div>
      </div>

      {/* Headline numbers. The six a leader asks about, nothing else. */}
      <div style={{ display: "grid", gap: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
        <Stat label="Contract value"
          value={ceiling ? fmtK(ceiling) : "n/a"}
          sub={contract.not_to_exceed ? "Not-to-exceed ceiling" : contract.value ? "Contract value" : "No ceiling set"}
          accent={T.gold} />
        <Stat label="Budget used"
          value={budget.percentUsed === null ? "n/a" : `${budget.percentUsed}%`}
          sub={ceiling ? `${fmtK(budget.spent)} of ${fmtK(ceiling)}` : "No ceiling set"}
          color={budget.color} accent={budget.color} />
        <Stat label="Deliverables"
          value={t.percentComplete === null ? "n/a" : `${t.percentComplete}%`}
          sub={`${t.complete} of ${t.total} complete`}
          accent={SERIES.complete} />
        <Stat label="Team on project"
          value={team.fte !== null ? `${team.fte} FTE` : team.headcount}
          sub={team.fte !== null
            ? `${team.headcount} ${team.headcount === 1 ? "person" : "people"}`
            : `${team.headcount} ${team.headcount === 1 ? "person" : "people"}, allocation not set`}
          accent={T.steel} />
        <Stat label="High risks"
          value={high.length}
          sub={`${openRisks.length} open, line at ${contract.high_risk_threshold ?? 15}`}
          color={high.length ? STATUS.overdue.ink : T.navy}
          accent={high.length ? STATUS.overdue.ink : T.steel} />
        <Stat label="Overdue CDs"
          value={overdueCDs}
          sub="Contract deliverables past due"
          color={overdueCDs ? STATUS.overdue.ink : T.navy}
          accent={overdueCDs ? STATUS.overdue.ink : T.steel} />
      </div>

      {/* Two charts: where the money sits, and where the work sits. */}
      <div style={{ display: "grid", gap: 16,
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
        <Panel title="Budget" accent={T.gold}>
          {!ceiling ? (
            <Empty text="No not-to-exceed or contract value set. Add one on the contract to see budget posture." />
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between",
                              fontSize: 12.5, color: T.soft, marginBottom: 6 }}>
                  <span>{fmtMoney(budget.spent)} committed</span>
                  <span>{budget.percentUsed}% of {fmtK(ceiling)}</span>
                </div>
                <Meter pct={budget.percentUsed} color={budget.color} height={16} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <MiniFig label="Spent" value={fmtK(budget.spent)} color={T.navy} />
                <MiniFig label="Labour cost" value={fmtK(budget.laborCost)} color={T.steel} />
                <MiniFig label="Remaining" value={fmtK(remaining)}
                  color={remaining <= 0 ? STATUS.overdue.ink : STATUS.complete.ink} />
              </div>
              {/* The number a sponsor actually asks for: where it lands, not
                  where it stands. A straight-line estimate, said as one. */}
              <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase",
                                  color: T.soft, fontWeight: 700 }}>Forecast at completion</div>
                    <div style={{ fontSize: 19, fontWeight: 700, marginTop: 3,
                                  fontVariantNumeric: "tabular-nums",
                                  color: forecast.level === "red" ? STATUS.overdue.ink
                                    : forecast.level === "amber" ? STATUS.inProgress.ink : T.ink }}>
                      {forecast.eac === null ? "Too early" : fmtK(forecast.eac)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 12.5,
                                color: forecast.level === "red" ? STATUS.overdue.ink
                                  : forecast.level === "amber" ? STATUS.inProgress.ink : T.soft }}>
                    {forecast.vac === null ? forecast.basis
                      : forecast.vac < 0 ? `${fmtK(forecast.overrun)} over ceiling`
                      : `${fmtK(forecast.vac)} under ceiling`}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: T.soft, marginTop: 6 }}>
                  Straight-line estimate from spend against {t.percentComplete ?? 0}% complete, not formal earned value.
                </div>
              </div>
              {invoices.pastTermsCount > 0 && (
                <div style={{ fontSize: 12.5, color: STATUS.overdue.ink, fontWeight: 600 }}>
                  {invoices.pastTermsCount} invoice{invoices.pastTermsCount === 1 ? "" : "s"}
                  {" "}past terms, {fmtK(invoices.pastTermsDollars)} outstanding.
                </div>
              )}
            </div>
          )}
        </Panel>

        <Panel title="Deliverables" accent={SERIES.complete}>
          {slices.length === 0 ? (
            <Empty text="No deliverables recorded yet." />
          ) : (
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ width: 170, height: 170 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={slices} dataKey="value" nameKey="name" cx="50%" cy="50%"
                         innerRadius={48} outerRadius={78} paddingAngle={2} stroke="none">
                      {slices.map((s, i) => <Cell key={i} fill={s.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "grid", gap: 8, flex: 1, minWidth: 140 }}>
                {slices.map((s) => (
                  <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                    <span style={{ width: 11, height: 11, borderRadius: 3, background: s.fill, flex: "none" }} />
                    <span style={{ color: T.ink, flex: 1 }}>{s.name}</span>
                    <span style={{ color: T.soft, fontVariantNumeric: "tabular-nums" }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* The team. The question this page exists to answer, given its own room. */}
      <Panel title="Team on this project" accent={T.steel}>
        {team.people.length === 0 ? (
          <Empty text="No resources recorded yet. Add people on the Resources register to show the team here." />
        ) : (
          <div style={{ display: "grid", gap: 2 }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(120px,1.4fr) 1fr 1fr",
                          gap: 12, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase",
                          color: T.soft, fontWeight: 700, padding: "0 0 8px" }}>
              <span>Person</span><span>Allocation</span><span>Hours burned</span>
            </div>
            {topTeam.map((p) => (
              <div key={p.id} style={{ display: "grid", gridTemplateColumns: "minmax(120px,1.4fr) 1fr 1fr",
                                       gap: 12, alignItems: "center", padding: "9px 0",
                                       borderTop: `1px solid ${T.line}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, overflowWrap: "anywhere" }}>{p.name}</div>
                  {p.role && <div style={{ fontSize: 11.5, color: T.soft }}>{p.role}</div>}
                </div>
                <div>
                  {p.allocation === null
                    ? <span style={{ fontSize: 12, color: T.soft }}>not set</span>
                    : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1 }}><Meter pct={p.allocation} color={T.steel} /></div>
                        <span style={{ fontSize: 12.5, color: T.ink, fontVariantNumeric: "tabular-nums",
                                       width: 36, textAlign: "right" }}>{p.allocation}%</span>
                      </div>
                    )}
                </div>
                <div>
                  {p.utilization === null
                    ? <span style={{ fontSize: 12, color: T.soft }}>no hours</span>
                    : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <Meter pct={Math.min(100, p.utilization)}
                                 color={p.utilization > 100 ? STATUS.overdue.ink
                                   : p.utilization >= 90 ? STATUS.inProgress.ink : STATUS.complete.ink} />
                        </div>
                        <span style={{ fontSize: 12.5, color: p.utilization > 100 ? STATUS.overdue.ink : T.ink,
                                       fontVariantNumeric: "tabular-nums", width: 40, textAlign: "right" }}>{p.utilization}%</span>
                      </div>
                    )}
                </div>
              </div>
            ))}
            {team.people.length > topTeam.length && (
              <div style={{ fontSize: 12, color: T.soft, paddingTop: 10 }}>
                and {team.people.length - topTeam.length} more on the Resources register.
              </div>
            )}
            <div style={{ fontSize: 11.5, color: T.soft, paddingTop: 12, lineHeight: 1.5 }}>
              Allocation is the share of a person committed to this project, entered on Resources.
              Hours burned is used against planned. They are different questions.
            </div>
          </div>
        )}
      </Panel>

      {/* Where the program sits in its own calendar, and what is next. */}
      <div style={{ display: "grid", gap: 16,
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
        {timeline && (
          <Panel title="Program timeline" accent={T.gold}>
            <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 8,
                          display: "flex", justifyContent: "space-between" }}>
              <span>{fmtDate(contract.start_date)}</span>
              <span>{timeline.pct}% elapsed</span>
              <span>{fmtDate(contract.end_date)}</span>
            </div>
            <Meter pct={timeline.pct} color={T.navy} height={14} />
            <div style={{ fontSize: 12, color: T.soft, marginTop: 10 }}>
              Calendar elapsed, not work complete. {t.percentComplete}% of deliverables are done.
            </div>
          </Panel>
        )}

        <Panel title="Next 60 days" accent={T.gold}>
          {upcoming.length === 0 ? <Empty text="Nothing due in the next 60 days." /> : (
            <div style={{ display: "grid", gap: 10 }}>
              {upcoming.slice(0, 6).map((u, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ width: 8, height: 8, marginTop: 5, flex: "none",
                                 background: u.kind === "milestone" ? T.gold : T.steel, borderRadius: 2 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, overflowWrap: "anywhere" }}>{u.title}</div>
                    <div style={{ fontSize: 11.5, color: T.soft }}>{u.date}{u.vendor ? ` · ${u.vendor}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {contract.summary && (
        <Panel title="Program summary">
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.65, color: T.ink }}>{contract.summary}</p>
        </Panel>
      )}

      <div style={{ fontSize: 11.5, color: T.soft, textAlign: "center" }}>
        A leadership view, computed live from the registers. For the detail behind any figure, open Contracts.
      </div>
    </div>
  );
}

function MiniFig({ label, value, color }) {
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.line}`, borderRadius: 5, padding: "10px 12px" }}>
      <div style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase",
                    color: T.soft, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color, marginTop: 3,
                    fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
